import React, { useState, useEffect } from "react";
import { Icon } from "../UIElements/Icon";
import Modal from "../UIElements/Modal";
import { useWalletTransactions } from "../../../hooks/useWalletTransactions";
import { useGlobalUtils } from "../../../lib/globalUtils";

const WalletTransactionModal = ({
  isOpen,
  onClose,
  wallet,
  userId,
  transactionType = "add", // 'add' or 'deduct'
}) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});

  const { walletTransactionMutation, isProcessing } =
    useWalletTransactions(userId);
  const { parsePrice } = useGlobalUtils();

  const isDeduction = transactionType === "deduct";

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setAmount("");
      setDescription("");
      setErrors({});
    }
  }, [isOpen]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate amount
    if (!amount || amount.trim() === "") {
      newErrors.amount = "Amount is required";
    } else {
      const numericAmount = parseFloat(amount);
      if (isNaN(numericAmount) || numericAmount <= 0) {
        newErrors.amount = "Amount must be a positive number";
      }
    }

    // Validate description (optional)
    if (description && description.trim().length > 100) {
      newErrors.description = "Description must be 100 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // For deductions, add negative sign to amount
      const transactionAmount = isDeduction ? `-${amount}` : amount;

      await walletTransactionMutation.mutateAsync({
        walletId: wallet.id,
        amount: transactionAmount,
        description: description.trim() || "",
      });

      // Close modal on success
      onClose();
    } catch (error) {
      // Error is handled by the hook
      console.error("Transaction failed:", error);
    }
  };

  // Handle amount input change
  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and one decimal point
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
      // Clear amount error when user starts typing
      if (errors.amount) {
        setErrors((prev) => ({ ...prev, amount: "" }));
      }
    }
  };

  // Handle description input change
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setDescription(value);
    // Clear description error when user starts typing
    if (errors.description) {
      setErrors((prev) => ({ ...prev, description: "" }));
    }
  };

  const getWalletDisplayName = (type) => {
    switch (type) {
      case "cash":
        return "Cash Wallet";
      case "credit_point":
        return "Credit Points";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const getWalletIcon = (type) => {
    switch (type) {
      case "cash":
        return "DollarSign";
      case "credit_point":
        return "Star";
      default:
        return "Wallet";
    }
  };

  const getWalletColor = (type) => {
    switch (type) {
      case "cash":
        return "text-green-400";
      case "credit_point":
        return "text-yellow-400";
      default:
        return "text-blue-400";
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${isDeduction ? "Deduct from" : "Add to"} ${getWalletDisplayName(wallet?.type)}`}
      subtitle={`${isDeduction ? "Remove" : "Add"} funds ${isDeduction ? "from" : "to"} this wallet`}
      maxWidth="max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Wallet Info */}
        <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-gray-700 p-2">
              <Icon
                name={getWalletIcon(wallet?.type)}
                className={`h-5 w-5 ${getWalletColor(wallet?.type)}`}
              />
            </div>
            <div>
              <h4 className="font-medium text-white">
                {getWalletDisplayName(wallet?.type)}
              </h4>
              <p className="text-sm text-gray-400">
                Current Balance:{" "}
                {wallet?.type === "credit_point"
                  ? `${wallet.balance} pts`
                  : parsePrice(wallet?.balance)}
              </p>
            </div>
          </div>
        </div>

        {/* Amount Input */}
        <div className="space-y-2">
          <label
            htmlFor="amount"
            className="block text-sm font-medium text-gray-300"
          >
            Amount {isDeduction ? "to Deduct" : "to Add"}
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-sm text-gray-400">
                {wallet?.type === "credit_point" ? "" : "$"}
              </span>
            </div>
            <input
              type="text"
              id="amount"
              value={amount}
              onChange={handleAmountChange}
              placeholder={wallet?.type === "credit_point" ? "0" : "0.00"}
              className={`w-full rounded-lg border bg-gray-800 py-2 pr-3 pl-8 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-purple-500 focus:outline-none ${
                errors.amount ? "border-red-500" : "border-gray-600"
              }`}
              disabled={isProcessing}
            />
            {wallet?.type === "credit_point" && (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <span className="text-sm text-gray-400">pts</span>
              </div>
            )}
          </div>
          {errors.amount && (
            <p className="text-xs text-red-400">{errors.amount}</p>
          )}
        </div>

        {/* Description Input */}
        <div className="space-y-2">
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-300"
          >
            Description <span className="text-gray-500">(Optional)</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={handleDescriptionChange}
            placeholder="Enter a description for this transaction..."
            rows={3}
            maxLength={100}
            className={`w-full resize-none rounded-lg border bg-gray-800 px-3 py-2 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-purple-500 focus:outline-none ${
              errors.description ? "border-red-500" : "border-gray-600"
            }`}
            disabled={isProcessing}
          />
          <div className="flex items-center justify-between">
            {errors.description && (
              <p className="text-xs text-red-400">{errors.description}</p>
            )}
            <p className="ml-auto text-xs text-gray-400">
              {description.length}/100 characters
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isProcessing || !amount}
            className={`flex flex-1 items-center justify-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              isDeduction
                ? "bg-red-600 hover:bg-red-700"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {isProcessing ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Icon
                  name={isDeduction ? "Minus" : "Plus"}
                  className="h-4 w-4"
                />
                <span>{isDeduction ? "Deduct Amount" : "Add Amount"}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default WalletTransactionModal;
